import { createSlice } from "@reduxjs/toolkit";
import { PayloadAction } from "@reduxjs/toolkit";
import api from "./api";
import { AppThunk } from "./app";
import { stringEncryption, setCookie, clearCookie } from "../config/Global";
import API_URL from "../config/ApiUrl";
import { loadMapApi } from "../components/Map/loadMapApi";

interface AuthState {
  appLoading: boolean;
  initialData: object | null;
  isLoading: boolean;
  mapScriptLoaded: boolean
  userDetail: any;
  token: string | null;
  profileProgress: any;
  sessionTimeOut: boolean;
}

const initialState = {
  appLoading: true,
  initialData: {},
  isLoading: false,
  mapScriptLoaded: false,
  userDetail: {},
  token: null,
  profileProgress: null,
  sessionTimeOut: false,
} as AuthState;

const AuthSlice = createSlice({
  name: "AUTH",
  initialState,
  reducers: {
    setAppLoading: (state, action: PayloadAction<boolean>) => {
      state.appLoading = action.payload;
    },
    setInitialData: (state, action: PayloadAction<any>) => {
      state.initialData = action.payload;
    },
    setUserToken: (state, action: PayloadAction<string | null>) => {
      // localStorage.setItem("token", action.payload ? action.payload : "");

      state.token = action.payload;
    },
    setUserDetail: (state, action: PayloadAction<any>) => {
      state.userDetail = action.payload;
    },
    setMapScriptLoaded: (state, action: PayloadAction<any>) => {
      state.mapScriptLoaded = action.payload;
    },
    resetAuthStore: () => {
      return initialState; // Reset the state to the initial state
    },
    start: (state) => {
      state.isLoading = true;
    },
    success: (state, action: PayloadAction<any>) => {
      if (action.payload?.token != undefined) {
        localStorage.setItem("token", action.payload.token);
      }
      state.isLoading = false;
    },
    failure: (state) => {
      state.isLoading = false;
    },
    setProfileProgress: (state, action: PayloadAction<any>) => {
      state.profileProgress = action.payload
    },
    setSessionTimeOut: (state, action: PayloadAction<any>) => {
      state.sessionTimeOut = action.payload;
    },
  },
});

export const {
  start,
  success,
  failure,
  resetAuthStore,
  setAppLoading,
  setInitialData,
  setUserDetail,
  setUserToken,
  setMapScriptLoaded,
  setProfileProgress,
  setSessionTimeOut
} = AuthSlice.actions;

// Async action to fetch todos from the API
export const doLogin =
  (action: any, type: string = 'normal'): AppThunk<any> =>
    async (dispatch) => {
      try {
        dispatch(start());
        var apiurl = '';
        if (type == 'direct') {
          apiurl = API_URL.LOGIN_DIRECT
        }
        else if (type == 'thinkific') {
          apiurl = API_URL.LOGIN_THINKIFIC
        }
        else {
          apiurl = API_URL.LOGIN
        }
        const response = await api.post(apiurl, action);
        if (action?.remember) {
          const string = JSON.stringify({
            email: action.username,
            password: action.password,
          });
          // localStorage.setItem("remember_me", );
          setCookie('remember_me', stringEncryption(string))
          // localStorage.setItem("remember_me", stringEncryption(string));
        } else {
          // localStorage.removeItem("remember_me");
          clearCookie('remember_me')
        }
        localStorage.setItem("timeIn", new Date().getTime().toString());
        localStorage.setItem("logintime", new Date().toString());
        dispatch(success(response.data));
        dispatch(setUserToken(response.data.token));
        dispatch(setUserOptions());
        return Promise.resolve(response.data);
      } catch (error: any) {
        dispatch(failure());
        return Promise.reject(error.data);
      }
    };

export const refreshToken =
  (): AppThunk<any> =>
    async (dispatch) => {
      try {
        dispatch(start());
        const response = await api.get(API_URL.REFRESH_TOKEN);
        localStorage.setItem("timeIn", new Date().getTime().toString());
        localStorage.setItem("last refreshtime", new Date().toString());
        dispatch(success(response.data.data));
        dispatch(setUserToken(response.data.data.token));
        return Promise.resolve(response.data);
      } catch (error: any) {
        dispatch(failure());
        return Promise.reject(error.data);
      }
    }
//Async action to fetch register api

export const doRegister =
  (action: any): AppThunk<any> =>
    async (dispatch) => {
      try {
        dispatch(start());
        const response = await api.post(API_URL.REGISTER, action);


        dispatch(success(response.data));
        dispatch(setUserToken(response.data.token));

        return Promise.resolve(response.data);
      } catch (error: any) {
        dispatch(failure());
        return Promise.reject(error.data);
      }
    };

// Async action to fetch sync from the API
export const initialAppOptions = (): AppThunk<any> => async (dispatch) => {
  try {
    const token = localStorage.getItem("token") ?? null;
    let initialData = sessionStorage.getItem("initialData") && sessionStorage.getItem("initialData") !== "undefined" ? JSON.parse(sessionStorage.getItem("initialData") ?? "") : null;

    if (token) {
      dispatch(setUserToken(token));
      dispatch(setUserOptions()).catch((error: any) => error)
    }

    const googleMapScript = await loadMapApi();
    if (googleMapScript) {
      googleMapScript.addEventListener("load", function () {
        dispatch(setMapScriptLoaded(true));
      });
    }

    if (!initialData) {
      const response = await api.post(API_URL.COMMON.SYNC);
      sessionStorage.setItem("initialData", JSON.stringify(response.data));
      dispatch(setAppLoading(false));

      initialData = response.data;
    }

    dispatch(setInitialData(initialData));

    dispatch(setAppLoading(false));
    return Promise.resolve(initialData);
  } catch (error: any) {
    dispatch(setAppLoading(false));
    return Promise.reject(error.data);
  }
};

export const setUserOptions = (): AppThunk<any> => async (dispatch) => {
  try {
    const response = await api.get(API_URL.ME);
    const userData = response.data.user
    const formField = [
      "decline_to_answer_gender",
      "decline_to_answer_disabilities",
      "decline_to_answer_veteran_status",
      "decline_to_answer_race_id",
      "decline_to_answer_household_income",
      "gender",
      "race_id",
      "disabilities",
      "veteran_status_id",
      "location",
      "name",
      "last_name",
      "email",
      "dob",
      "mobile_no",
      "house_hold_income",
      "education_level",
      "year_of_experience"
    ]
    const keys = Object.keys(userData);
    const value = []
    for (const key of keys) {

      if (formField.includes(key)) {
        value.push(response.data.user[key])

        const filterData: any = value.filter((item: any) => item !== null && item !== 0)
        let newProgress: any
        if (filterData.length >= 10 && (userData.education_level === null || userData.year_of_experience === null)) {
          newProgress = 80;
        }
        // else if (filterData.length >= 10 && userData.mobile_no === null) {
        //   newProgress = 90;
        // }
        else {
          newProgress = (filterData.length / 10) * 100;
        }
        dispatch(setProfileProgress(newProgress))
      }
    }
    var CustomeUser = response.data.user;
    CustomeUser.isFreeUser = response.data.user.purchased == 0;
    // console.log('me', CustomeUser.isFreeUser)
    if (CustomeUser.exam_flag == 1) {
      if (CustomeUser.interest_assessment_exam_status == 0) {
        if (CustomeUser.skill_assessment_exam_status == 1) {
          CustomeUser.skill_assessment_exam_status = 1;
        }
        else if (CustomeUser.skill_assessment_exam_status == 0) {
          CustomeUser.skill_assessment_exam_status = 0;
        }
        else {
          CustomeUser.skill_assessment_exam_status = 3;

        }
      }
      CustomeUser.interest_assessment_exam_status = 2;
      // CustomeUser.interest_assessment_exam_status = 0;

    }
    // console.log('CustomeUser', CustomeUser);
    dispatch(setAppLoading(false));
    dispatch(setUserDetail(CustomeUser));
    return Promise.resolve(response.data);
  } catch (error: any) {
    if (localStorage.getItem('token') == '') {
      dispatch(setUserToken(null));
      localStorage.removeItem("token");
    }
    dispatch(setAppLoading(false));
    return Promise.reject(error.data);
  }
};

export const doLogout = (): AppThunk<any> => async (dispatch) => {
  try {
    const response = await api.get(API_URL.LOGOUT);
    dispatch(setUserToken(null));
    dispatch(setUserDetail(null));
    dispatch(setInitialData(null));
    localStorage.removeItem("token");
    localStorage.removeItem("timeIn");
    localStorage.removeItem("timeOut");
    localStorage.removeItem("feedback");
    initialAppOptions();
    return Promise.resolve(response.data);
  } catch (error: any) {
    return Promise.reject(error.data);
  }
};

export const forgotPassword =
  (action: any): AppThunk<any> =>
    async (dispatch) => {
      try {
        dispatch(start());
        const response = await api.post(API_URL.FORGOT_PASSWORD, action);
        dispatch(success(response.data));

        return Promise.resolve(response.data);
      } catch (error: any) {
        dispatch(failure());
        return Promise.reject(error.data);
      }
    };

export const updatePassword =
  (otp: string, action: any): AppThunk<any> =>
    async (dispatch) => {
      try {
        dispatch(start());
        const response = await api.post(API_URL.RESET_PASSWORD(otp), action);
        dispatch(success(response.data));

        return Promise.resolve(response.data);
      } catch (error: any) {
        dispatch(failure());
        return Promise.reject(error.data);
      }
    };
export const verifyOtp =
  (data: any): AppThunk<any> =>
    async (dispatch) => {
      try {
        dispatch(start());
        const response = await api.post(API_URL.VERIFY_PASSWORD,data);
        dispatch(success(response.data));

        return Promise.resolve(response.data);
      } catch (error: any) {
        dispatch(failure());
        return Promise.reject(error.data);
      }
    };

export const changePassword =
  (payload: any): AppThunk<any> =>
    async (dispatch) => {
      try {
        dispatch(start());
        const response = await api.post(API_URL.CHANGE_PASSWORD, payload);

        dispatch(success(response.data));
        return Promise.resolve(response.data);
      } catch (error: any) {
        dispatch(failure());
        return Promise.reject(error.data);
      }
    };
export const deleteAccount = (): AppThunk<any> => async (dispatch) => {
  try {
    const response = await api.delete(API_URL.DELETE_ACCOUNT);
    dispatch(setUserToken(null));
    dispatch(setUserDetail(null));
    dispatch(setInitialData(null));
    localStorage.removeItem("token");
    localStorage.removeItem("timeIn");
    localStorage.removeItem("timeOut");
    initialAppOptions();
    return Promise.resolve(response.data);
  } catch (error: any) {
    return Promise.reject(error.data);
  }
};
export const verifyEmail = (data: string): AppThunk<any> =>
  async (dispatch) => {
    try {
      dispatch(start());
      const response = await api.get(API_URL.VERIFY_TOKEN(data));
      dispatch(success(response.data));
      return Promise.resolve(response.data);
    } catch (error: any) {
      dispatch(failure());
      return Promise.reject(error.data);
    }
  };



export const doRegisterInvitedUser =
  (user_id: string, action: any): AppThunk<any> =>
    async (dispatch) => {
      try {
        dispatch(start());
        const response = await api.post(API_URL.USER_REGISTER(user_id), action);


        dispatch(success(response.data));
        dispatch(setUserToken(response.data.token));

        return Promise.resolve(response.data);
      } catch (error: any) {
        dispatch(failure());
        return Promise.reject(error.data);
      }
    };


export const getRegisterDetails =
  (user_id: string): AppThunk<any> =>
    async (dispatch) => {
      try {
        dispatch(start());
        const response = await api.get(API_URL.GET_USER(user_id));
        dispatch(success(response.data));
        return Promise.resolve(response.data.data);
      } catch (error: any) {
        dispatch(failure());
        return Promise.reject(error.data);
      }
    };

export const getAccessCodeDetails =
  (access_code_id: string): AppThunk<any> =>
    async () => {
      try {
        const response = await api.get(API_URL.GET_ACCESS_CODE(access_code_id));
        return Promise.resolve(response.data.data);
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    };
export const sendUserOtp =
  (requestData: any): AppThunk<any> =>
    async () => {
      try {
        const response = await api.post(API_URL.SEND_OTP, requestData);
        return Promise.resolve(response.data.data);
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    };



export const thinkificCreatePassword =
  (action: any): AppThunk<any> =>
    async (dispatch) => {
      try {
        dispatch(start());
        const response = await api.post(API_URL.THINKIFIC_CREATE_PASSWORD, action);
        dispatch(success(response.data));
        return Promise.resolve(response.data);
      } catch (error: any) {
        dispatch(failure());
        return Promise.reject(error.data);
      }
    };


const AuthSliceReducer = AuthSlice.reducer;
export default AuthSliceReducer;
